import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Pattern, PatternContext } from '../../contexts/PatternContext'
import { PatternTreeContext } from '../../contexts/PatternTreeContext'
import { useCopilotHighlights } from '../contexts/PatternCopilotContext'
import { CopilotDraftHost, CopilotTurnContext, DraftApplyResult, DraftEnvelope } from '../types'
import { patternToAuthoringForm } from '../utils/authoringForm'
import { normalizeAgentDraft } from '../utils/normalizeAgentDraft'
import { diffPatternDrafts } from '../utils/diffPatternDrafts'

const UNDO_STACK_DEPTH = 10

interface UndoEntry {
  snapshot: Pattern
  toolCallId: string
}

interface PendingDraft {
  envelope: DraftEnvelope
  toolCallId: string
}

/**
 * CopilotDraftHost implementation over the open pattern editor. The ONLY
 * pattern-aware seam between the (host-agnostic) copilot panel and
 * PatternTreeContext. Applies drafts via the same raw setPattern path
 * VersionTab uses.
 *
 * Attribution model: agent applies/reverts record the exact object they pass
 * to setPattern in expectedAgentPatternRef and update the local refs
 * SYNCHRONOUSLY; the [pattern] effect then attributes any commit that is NOT
 * that exact object to the user. Identity comparison makes this immune to
 * batching (a user edit batched with an agent apply produces a different
 * final object and is correctly booked as a user edit) and cannot get stuck
 * the way a boolean flag can when React bails out on identical state.
 */
export default function usePatternCopilotHost(): CopilotDraftHost {
  const { pattern, setPattern, newlyCreatedConditionIds } = useContext(PatternTreeContext)
  const { getIndicatorByValue } = useContext(PatternContext)
  const { flashConditions } = useCopilotHighlights()

  const patternRef = useRef<Pattern>(pattern)
  const revisionRef = useRef(0)
  const lastUserEditRevisionRef = useRef(0)
  const dirtySinceLastAgentApplyRef = useRef(false)
  const expectedAgentPatternRef = useRef<Pattern | null>(null)
  const undoStackRef = useRef<UndoEntry[]>([])
  const appliedToolCallIdsRef = useRef<Set<string>>(new Set())
  const pendingDraftRef = useRef<PendingDraft | null>(null)

  // Single redo slot: the state a revert threw away, until the next apply
  const redoRef = useRef<{ snapshot: Pattern; entry: UndoEntry } | null>(null)

  const [pendingDraft, setPendingDraftState] = useState<PendingDraft | null>(null)
  const [validationWarnings, setValidationWarnings] = useState<string[]>([])
  const [lastAppliedToolCallId, setLastAppliedToolCallId] = useState<string | null>(null)
  const [lastRevertedToolCallId, setLastRevertedToolCallId] = useState<string | null>(null)

  const setPendingDraft = useCallback((value: PendingDraft | null) => {
    pendingDraftRef.current = value
    setPendingDraftState(value)
  }, [])

  // Attribution of commits the host did NOT make itself (see model above)
  useEffect(() => {
    if (pattern === expectedAgentPatternRef.current) {
      expectedAgentPatternRef.current = null
      return
    }
    if (patternRef.current === pattern) return
    patternRef.current = pattern
    revisionRef.current += 1
    lastUserEditRevisionRef.current = revisionRef.current
    dirtySinceLastAgentApplyRef.current = true
  }, [pattern])

  const getTurnContext = useCallback(
    (): CopilotTurnContext => ({
      entityType: 'pattern',
      entityId: patternRef.current.id,
      revision: revisionRef.current,
      dirtySinceLastAgentApply: dirtySinceLastAgentApplyRef.current,
      draftJson: JSON.stringify(patternToAuthoringForm(patternRef.current)),
    }),
    []
  )

  const performApply = useCallback(
    (envelope: DraftEnvelope, toolCallId: string): DraftApplyResult => {
      const before = patternRef.current
      const normalized = normalizeAgentDraft(envelope.draft, before, getIndicatorByValue)
      if (!normalized.ok) {
        return { status: 'rejected', reason: 'bad-envelope', detail: normalized.reason }
      }

      undoStackRef.current = [
        ...undoStackRef.current.slice(-(UNDO_STACK_DEPTH - 1)),
        { snapshot: before, toolCallId },
      ]
      appliedToolCallIdsRef.current.add(toolCallId)
      setLastAppliedToolCallId(toolCallId)
      // A new apply supersedes whatever a previous undo could restore
      redoRef.current = null
      setLastRevertedToolCallId(null)

      // Synchronous bookkeeping so same-turn applies diff/snapshot correctly
      expectedAgentPatternRef.current = normalized.pattern
      patternRef.current = normalized.pattern
      revisionRef.current += 1
      dirtySinceLastAgentApplyRef.current = false
      setPattern(normalized.pattern)

      const diff = diffPatternDrafts(before, normalized.pattern)
      // Auto-open newly added conditions (the existing add-condition affordance)
      diff.addedConditionIds.forEach((id) => newlyCreatedConditionIds.current.add(id))
      flashConditions([...diff.addedConditionIds, ...diff.modifiedConditionIds])
      setValidationWarnings(normalized.warnings)

      return {
        status: 'applied',
        revision: revisionRef.current,
        diff,
        validationWarnings: normalized.warnings,
      }
    },
    [getIndicatorByValue, setPattern, newlyCreatedConditionIds, flashConditions]
  )

  const applyDraft = useCallback(
    (
      envelope: DraftEnvelope,
      meta: { toolCallId: string; turnStartRevision: number }
    ): DraftApplyResult => {
      if (appliedToolCallIdsRef.current.has(meta.toolCallId)) {
        return { status: 'rejected', reason: 'duplicate' }
      }
      if (envelope.entityType !== 'pattern') {
        return { status: 'rejected', reason: 'entity-mismatch', detail: envelope.entityType }
      }
      // Mid-turn hand edits win: hold the draft behind the banner (latest-wins)
      if (lastUserEditRevisionRef.current > meta.turnStartRevision) {
        setPendingDraft({ envelope, toolCallId: meta.toolCallId })
        return { status: 'held', reason: 'user-edited-mid-turn' }
      }
      return performApply(envelope, meta.toolCallId)
    },
    [performApply, setPendingDraft]
  )

  const revertLastApply = useCallback((): boolean => {
    const entry = undoStackRef.current.pop()
    if (!entry) return false
    // What the revert throws away (the AI change plus any later edits) is
    // exactly what redo restores
    redoRef.current = { snapshot: patternRef.current, entry }
    setLastRevertedToolCallId(entry.toolCallId)
    // A revert is an explicit user rejection: book it as a user edit so later
    // drafts in the same turn hit the hold banner and the next turn re-sends
    // the true state
    expectedAgentPatternRef.current = null
    patternRef.current = entry.snapshot
    revisionRef.current += 1
    lastUserEditRevisionRef.current = revisionRef.current
    dirtySinceLastAgentApplyRef.current = true
    setPattern(entry.snapshot)
    setLastAppliedToolCallId(
      undoStackRef.current.length > 0
        ? undoStackRef.current[undoStackRef.current.length - 1].toolCallId
        : null
    )
    return true
  }, [setPattern])

  const redoLastRevert = useCallback((): boolean => {
    const redo = redoRef.current
    if (!redo) return false
    redoRef.current = null
    setLastRevertedToolCallId(null)
    // Like the revert, redo is a user decision — book it as a user edit so
    // the next turn re-sends the true state
    expectedAgentPatternRef.current = null
    patternRef.current = redo.snapshot
    revisionRef.current += 1
    lastUserEditRevisionRef.current = revisionRef.current
    dirtySinceLastAgentApplyRef.current = true
    setPattern(redo.snapshot)
    // The apply is live again, so its Undo comes back
    undoStackRef.current = [...undoStackRef.current, redo.entry]
    setLastAppliedToolCallId(redo.entry.toolCallId)
    return true
  }, [setPattern])

  const resolvePendingDraft = useCallback(
    (
      action: 'apply' | 'discard'
    ): { toolCallId: string; result: DraftApplyResult | null } | null => {
      const current = pendingDraftRef.current
      setPendingDraft(null)
      if (!current) return null
      // Discard still identifies the toolCallId so its transcript card can
      // leave the perpetual "held" state
      if (action === 'discard') return { toolCallId: current.toolCallId, result: null }
      // Runs outside any state updater (StrictMode double-invocation safe)
      return {
        toolCallId: current.toolCallId,
        result: performApply(current.envelope, current.toolCallId),
      }
    },
    [performApply, setPendingDraft]
  )

  return useMemo(
    () => ({
      entityType: 'pattern' as const,
      entityId: pattern.id ?? null,
      entityName: pattern.name,
      getTurnContext,
      applyDraft,
      revertLastApply,
      redoLastRevert,
      resolvePendingDraft,
      pendingDraftSummary: pendingDraft
        ? (pendingDraft.envelope.summary ?? 'AI proposed a new version of this pattern')
        : null,
      canRevert: lastAppliedToolCallId !== null,
      lastAppliedToolCallId,
      lastRevertedToolCallId,
      validationWarnings,
    }),
    [
      pattern.id,
      pattern.name,
      getTurnContext,
      applyDraft,
      revertLastApply,
      redoLastRevert,
      resolvePendingDraft,
      pendingDraft,
      lastAppliedToolCallId,
      lastRevertedToolCallId,
      validationWarnings,
    ]
  )
}
